/**
 * feat-298 — the disclosure primitive for Track / Lesson / Drill sections
 * (spec §4, design NOTES §6 "Disclosure semantics").
 *
 * The toggle is ONLY the chevron square, never the title: that keeps text
 * selection, and feat-299's future inline-edit affordances, free.
 *
 * Collapsed children are UNMOUNTED by the caller, not hidden. `display:none`
 * elements report `rect.top = 0`, which `captureScrollAnchor` would mistake for
 * "the first item below the nav" (NOTES §13).
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';
import { QUIZ_REVIEW_NAV_COPY as COPY } from './navCopy';

export const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40';

/** A stable, id-safe region id for a section key. */
export const regionId = (key: string): string => `review-region-${key}`;

export const Chevron: React.FC<{ open: boolean; className: string }> = ({ open, className }) => (
  <span
    aria-hidden
    className={`inline-grid place-items-center transition-transform duration-150 motion-reduce:transition-none${
      open ? ' rotate-90' : ''
    }`}
  >
    <ChevronRight className={className} aria-hidden />
  </span>
);

export interface DisclosureButtonProps {
  open: boolean;
  onToggle: () => void;
  /** Id of the region this button shows and hides. */
  controls: string;
  /** The section's full name, e.g. "Lesson 3: Upselling" — becomes
   *  "Collapse Lesson 3: Upselling" / "Expand …". */
  title: string;
  /** Size, colour and position classes for the hit area. */
  className: string;
  iconClassName: string;
}

export const DisclosureButton: React.FC<DisclosureButtonProps> = ({
  open,
  onToggle,
  controls,
  title,
  className,
  iconClassName,
}) => (
  <button
    type="button"
    aria-expanded={open}
    aria-controls={controls}
    aria-label={open ? COPY.collapse(title) : COPY.expand(title)}
    title={open ? COPY.collapse(title) : COPY.expand(title)}
    onClick={onToggle}
    className={`shrink-0 grid place-items-center rounded-md ${className} ${FOCUS_RING}`}
  >
    <Chevron open={open} className={iconClassName} />
  </button>
);
